import { createGlobalState } from 'react-use';
import { useCallback } from 'react';

import { PreInterviewStatus } from '@dreryk/egabinet-pre-interview-frontend-api';

type AppointmentId = number;
export type PreInterviewState = { status: PreInterviewStatus; surveyId: number };
type PreInterviewMap = Array<[AppointmentId, PreInterviewState | null]>;

type PreInterviewStateGetter = (appointmentId: AppointmentId) => PreInterviewState | null | undefined;
type PreInterviewStateUpdater = (newState: PreInterviewMap) => void;
type PreInterviewStateSingleUpdater = (appointmentId: AppointmentId, state: PreInterviewState | null) => void;
type PreInterviewStateReset = VoidFunction;

const useGlobalState = createGlobalState(new Map<AppointmentId, PreInterviewState | null>());

type UsePreInterviewGlobalStateReturnFn = {
   getPreInterviewState: PreInterviewStateGetter;
   updatePreInterviewState: PreInterviewStateUpdater;
   resetPreInterviewState: PreInterviewStateReset;
   updateSinglePreInterviewState: PreInterviewStateSingleUpdater;
};

export const usePreInterviewGlobalState = (): UsePreInterviewGlobalStateReturnFn => {
   const [globalState, setGlobalState] = useGlobalState();

   const getPreInterviewState: PreInterviewStateGetter = useCallback(
      (appointmentId) => {
         return globalState.has(appointmentId) ? globalState.get(appointmentId) : undefined;
      },
      [globalState]
   );

   const resetPreInterviewState: PreInterviewStateReset = useCallback(() => {
      setGlobalState(new Map());
   }, [setGlobalState]);

   const updatePreInterviewState: PreInterviewStateUpdater = useCallback(
      (newState) => {
         setGlobalState((prevState) => new Map([...prevState, ...newState]));
      },
      [setGlobalState]
   );

   const updateSinglePreInterviewState: PreInterviewStateSingleUpdater = useCallback(
      (appointmentId, state) => {
         updatePreInterviewState([[appointmentId, state]]);
      },
      [updatePreInterviewState]
   );

   return {
      getPreInterviewState: getPreInterviewState,
      updatePreInterviewState: updatePreInterviewState,
      resetPreInterviewState: resetPreInterviewState,
      updateSinglePreInterviewState: updateSinglePreInterviewState,
   };
};
